'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shared/components/shadcn';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FieldMessage } from '../../../form-controls/FieldMessage/FieldMessage';
import { ToggleGroup, type ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';

const allowedValues = ['lottery', 'casino', 'sportsbook'] as const;
type AllowedValue = (typeof allowedValues)[number];

type ContactPreferencesFormProps = {
  initialSelected: AllowedValue[];
  selected?: AllowedValue[];
  onSelectedChange?: (selected: AllowedValue[]) => void;
  options: ToggleOption[];
  onSubmit: (selected: AllowedValue[]) => Promise<void>;
};

type FormData = {
  selected: AllowedValue[];
};

function arraysEqualAsSets(firstArray: string[], secondArray: string[]): boolean {
  if (firstArray.length !== secondArray.length) return false;
  const firstSet = new Set(firstArray);
  const secondSet = new Set(secondArray);
  if (firstSet.size !== secondSet.size) return false;
  for (const value of firstSet) {
    if (!secondSet.has(value)) return false;
  }
  return true;
}

function ContactPreferencesForm({ initialSelected, selected, onSelectedChange, options, onSubmit }: ContactPreferencesFormProps) {
  const schema = useMemo(
    () =>
      z.object({
        selected: z.array(z.enum(allowedValues)),
      }),
    []
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { selected: initialSelected },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const { setValue } = form;
  const isControlled = selected !== undefined;

  useEffect(() => {
    if (isControlled) return;
    setValue('selected', initialSelected);
  }, [initialSelected, isControlled, setValue]);

  const currentSelected = isControlled ? selected : form.watch('selected') || [];
  const hasChanges = !arraysEqualAsSets(currentSelected, initialSelected);

  const updateSelected = (next: AllowedValue[]) => {
    form.setValue('selected', next);
    onSelectedChange?.(next);
  };

  const handleSubmit = form.handleSubmit(async values => {
    const submitted = selected ?? values.selected ?? [];
    await onSubmit(submitted);
  });

  const hasError = !!form.formState.errors.selected?.message;

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6" data-track-feature="My Account" data-track-container="Products">
        <ToggleGroup
          value={currentSelected}
          onValueChange={values => updateSelected(values as AllowedValue[])}
          options={options}
          hasError={hasError}
          data-track-group="Product Preferences"
        />
        <FieldMessage
          id="contact-preferences-error"
          message={form.formState.errors.selected?.message}
          messageType={hasError ? 'error' : undefined}
          data-track-helper="Contact Preferences"
        />
        <div className="mt-auto flex" data-track-container="Actions">
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSubmit}
            disabled={!hasChanges}
            data-testid="product-preferences-update-button"
            data-track-name="Update Product Preferences"
          >
            Update
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}

export { ContactPreferencesForm };
